import React, { useState, useRef, useCallback } from 'react';
import { Base64ImageService } from '../../lib/services/base64ImageService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType, ImageFormat, ImageResizeOptions } from '../../lib/types';
import './Base64ImageEncoder.css';

export const Base64ImageEncoder: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [base64Output, setBase64Output] = useState('');
  const [rawBase64Data, setRawBase64Data] = useState(''); // Store raw base64 for reformatting
  const [previewUrl, setPreviewUrl] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [outputFormat, setOutputFormat] = useState<ImageFormat>(ImageFormat.Base64);
  const [resizeOptions, setResizeOptions] = useState<ImageResizeOptions | null>(null);
  const [quality, setQuality] = useState(90);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setIsLoading(true);
    setError(null);

    try {
      if (mode === 'encode') {
        const config = Base64ImageService.createConfiguration('encode', {
          inputFile: file,
          outputFormat: outputFormat as any, // Convert ImageFormat to string
          quality: quality / 100, // Convert percentage to 0-1 range
          maxWidth: resizeOptions?.width,
          maxHeight: resizeOptions?.height,
          preserveAspectRatio: resizeOptions?.maintainAspectRatio ?? true
        });

        const result = await Base64ImageService.encode(config);
        const base64Data = result.outputData as string;

        // Extract just the base64 part for storage, create proper data URL for DataURL format
        const matches = base64Data.match(/^data:image\/[^;]+;base64,(.+)$/);
        const rawBase64 = matches ? matches[1] : base64Data;

        setRawBase64Data(rawBase64);
        const formattedOutput = formatOutput(base64Data, outputFormat, file);
        setBase64Output(formattedOutput);

        // Set preview for image files
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
        }
      } else {
        // Decode mode - handle base64 to image
        const config = Base64ImageService.createConfiguration('decode', {
          inputBase64: base64Output,
          outputFormat: 'png'
        });

        const result = await Base64ImageService.decode(config);

        // Create download link for decoded image
        const file = result.outputData as File;
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.BASE64_IMAGE);
      setError(errorMessage);
      setBase64Output('');
      setPreviewUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, []);

  const handleCopy = async () => {
    if (!base64Output) return;

    try {
      const success = await ClipboardUtils.copyText(base64Output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const handleBase64Input = async (input: string) => {
    setBase64Output(input);
    if (!input.trim()) {
      setPreviewUrl('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const config = Base64ImageService.createConfiguration('decode', {
        inputBase64: input,
        outputFormat: 'png'
      });
      const result = await Base64ImageService.decode(config);

      const file = result.outputData as File;
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.BASE64_IMAGE);
      setError(errorMessage);
      setPreviewUrl('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;

    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = selectedFile ? `encoded_${selectedFile.name}` : 'decoded_image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearAll = () => {
    setSelectedFile(null);
    setBase64Output('');
    setRawBase64Data('');
    setPreviewUrl('');
    setError(null);
    setCopyFeedback('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Reformat output when format changes
  React.useEffect(() => {
    if (rawBase64Data && selectedFile && mode === 'encode') {
      const formattedOutput = formatOutput(rawBase64Data, outputFormat, selectedFile);
      setBase64Output(formattedOutput);
    }
  }, [outputFormat, rawBase64Data, selectedFile, mode]);

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Base64';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'copy-button';
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatOutput = (base64Data: string, format: ImageFormat, file: File): string => {
    // Extract raw base64 if it's in data URL format
    const matches = base64Data.match(/^data:image\/[^;]+;base64,(.+)$/);
    const rawBase64 = matches ? matches[1] : base64Data;

    switch (format) {
      case ImageFormat.Base64:
        // Return just the base64 part
        return rawBase64;

      case ImageFormat.DataURL:
        // Create proper data URL with MIME type from file
        const mimeType = file.type || 'image/png';
        return `data:${mimeType};base64,${rawBase64}`;

      case ImageFormat.HTML:
        // Create HTML img tag
        const htmlSrc = `data:${file.type || 'image/png'};base64,${rawBase64}`;
        return `<img src="${htmlSrc}" alt="${file.name}" />`;

      case ImageFormat.CSS:
        // Create CSS background-image
        const cssSrc = `data:${file.type || 'image/png'};base64,${rawBase64}`;
        return `background-image: url('${cssSrc}');`;

      default:
        return rawBase64;
    }
  };

  return (
    <div className="base64-image-encoder">
      <div style={{ marginBottom: '2rem' }}>
        <h1>Base64 Image Encoder/Decoder</h1>
        <p>
          Convert images to Base64 strings for embedding in HTML/CSS or decode Base64 data back to images
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label htmlFor="mode">Operation Mode</label>
          <select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            data-testid="mode-select"
          >
            <option value="encode">Encode Image to Base64</option>
            <option value="decode">Decode Base64 to Image</option>
          </select>
        </div>

        {mode === 'encode' && (
          <>
            <div className="form-group">
              <label htmlFor="output-format">Output Format</label>
              <select
                id="output-format"
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value as ImageFormat)}
                data-testid="output-format-select"
              >
                <option value={ImageFormat.Base64}>Base64 String</option>
                <option value={ImageFormat.DataURL}>Data URL</option>
                <option value={ImageFormat.HTML}>HTML Image Tag</option>
                <option value={ImageFormat.CSS}>CSS Background</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="quality">Image Quality ({quality}%)</label>
              <input
                type="range"
                id="quality"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="quality-slider"
                data-testid="quality-slider"
              />
              <span className="quality-value">{quality}%</span>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!resizeOptions}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setResizeOptions({ width: 800, height: 600, maintainAspectRatio: true });
                    } else {
                      setResizeOptions(null);
                    }
                  }}
                  data-testid="resize-checkbox"
                />
                Resize Image
              </label>
            </div>

            {resizeOptions && (
              <div className="resize-options">
                <div className="form-group">
                  <label htmlFor="resize-width">Width (px)</label>
                  <input
                    type="number"
                    id="resize-width"
                    value={resizeOptions.width}
                    onChange={(e) => setResizeOptions({ ...resizeOptions, width: Number(e.target.value) })}
                    min="1"
                    max="4000"
                    data-testid="resize-width"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="resize-height">Height (px)</label>
                  <input
                    type="number"
                    id="resize-height"
                    value={resizeOptions.height}
                    onChange={(e) => setResizeOptions({ ...resizeOptions, height: Number(e.target.value) })}
                    min="1"
                    max="4000"
                    data-testid="resize-height"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={resizeOptions.maintainAspectRatio}
                      onChange={(e) => setResizeOptions({ ...resizeOptions, maintainAspectRatio: e.target.checked })}
                      data-testid="maintain-aspect-checkbox"
                    />
                    Maintain Aspect Ratio
                  </label>
                </div>
              </div>
            )}

            <div className="upload-section">
              <div
                className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                data-testid="drop-zone"
              >
                <div className="drop-zone-content">
                  <div className="upload-icon">📁</div>
                  <p>Drag and drop an image here, or</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="file-input"
                    data-testid="file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="browse-button"
                    data-testid="browse-button"
                  >
                    Browse Files
                  </button>
                </div>
              </div>

              {selectedFile && (
                <div className="file-info" data-testid="file-info">
                  <h4>Selected File:</h4>
                  <p><strong>Name:</strong> {selectedFile.name}</p>
                  <p><strong>Size:</strong> {formatFileSize(selectedFile.size)}</p>
                  <p><strong>Type:</strong> {selectedFile.type}</p>
                  <p><strong>Last Modified:</strong> {new Date(selectedFile.lastModified).toLocaleString()}</p>
                </div>
              )}
            </div>
          </>
        )}

        {mode === 'decode' && (
          <div className="form-group">
            <label htmlFor="base64-input">Base64 Input</label>
            <textarea
              id="base64-input"
              value={base64Output}
              onChange={(e) => handleBase64Input(e.target.value)}
              placeholder="Paste Base64 image data here..."
              className="base64-textarea"
              rows={8}
              data-testid="base64-input"
            />
          </div>
        )}

        {error && (
          <div className="error-message" data-testid="error-message">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator" data-testid="loading-indicator">
            Processing image...
          </div>
        )}

        {previewUrl && (
          <div className="preview-section">
            <h3>{mode === 'encode' ? 'Original Image' : 'Decoded Image'}</h3>
            <div className="image-preview">
              <img
                src={previewUrl}
                alt={mode === 'encode' ? 'Original' : 'Decoded'}
                data-testid="image-preview"
              />
            </div>
            <button
              onClick={handleDownload}
              className="download-button"
              data-testid="download-button"
            >
              Download {mode === 'encode' ? 'Original' : 'Decoded'} Image
            </button>
          </div>
        )}

        {base64Output && mode === 'encode' && (
          <div className="output-section">
            <h3>Base64 Output</h3>
            <textarea
              value={base64Output}
              readOnly
              className="base64-output"
              rows={8}
              data-testid="base64-output"
            />
            <div className="output-actions">
              <button
                ref={copyButtonRef}
                onClick={handleCopy}
                className={getButtonClass()}
                data-testid="copy-button"
              >
                {getButtonText()}
              </button>
              <button
                onClick={clearAll}
                className="clear-button"
                data-testid="clear-button"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        <div className="info-section">
          <h3>Supported Formats</h3>
          <div className="format-grid">
            <div className="format-item">
              <strong>Input:</strong> JPEG, PNG, GIF, WebP, BMP, SVG
            </div>
            <div className="format-item">
              <strong>Output:</strong> Base64, Data URL, HTML, CSS
            </div>
            <div className="format-item">
              <strong>Max Size:</strong> 10MB per image
            </div>
            <div className="format-item">
              <strong>Privacy:</strong> All processing happens in your browser
            </div>
          </div>
        </div>
      </div>

      {/* No-JS Fallback */}
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>This tool requires JavaScript to process images. Please enable JavaScript to use the Base64 Image Encoder/Decoder.</p>
          <p>For Base64 encoding without JavaScript, you can use online tools or command-line utilities like:</p>
          <pre><code>base64 input.jpg &gt; output.txt</code></pre>
        </div>
      </noscript>
    </div>
  );
};